import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";

import InventoryService from "../../api/inventory";
import StockEntryModal from "./modals/StockEntryModal";
import StockAdjustmentModal from "./modals/StockAdjustmentModal";

const TYPE_COLORS = {
  purchase: "success",
  order_deduction: "info",
  manual_adjustment: "warning",
  loss: "error",
};

const TYPE_LABELS = {
  purchase: "Compra",
  order_deduction: "Saida por venda",
  manual_adjustment: "Ajuste manual",
  loss: "Perda",
};

const MovementsPage = ({ showAlert }) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: rowsPerPage };
      if (typeFilter) params.movement_type = typeFilter;
      if (startDate) params.start_date = dayjs(startDate).format("YYYY-MM-DD");
      if (endDate) params.end_date = dayjs(endDate).format("YYYY-MM-DD");

      const res = await InventoryService.getStockMovements(params);
      setMovements(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      showAlert("Erro ao carregar movimentacoes", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, typeFilter, startDate, endDate, showAlert]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleEntrySuccess = () => {
    setEntryModalOpen(false);
    showAlert("Entrada de estoque registrada com sucesso!");
    fetchMovements();
  };

  const handleAdjustmentSuccess = () => {
    setAdjustmentModalOpen(false);
    showAlert("Ajuste de estoque registrado com sucesso!");
    fetchMovements();
  };

  const formatNumber = (val) =>
    Number(val).toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  const formatMoney = (val) => {
    if (val === null || val === undefined) return "-";
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Movimentacoes de Estoque
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEntryModalOpen(true)}
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Entrada
          </Button>
          <Button
            variant="outlined"
            startIcon={<RemoveIcon />}
            onClick={() => setAdjustmentModalOpen(true)}
            sx={{
              borderColor: "#f59e0b",
              color: "#f59e0b",
              "&:hover": {
                borderColor: "#d97706",
                backgroundColor: alpha("#f59e0b", 0.04),
              },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Ajuste / Perda
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={typeFilter}
            label="Tipo"
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DatePicker
          label="Data inicio"
          value={startDate}
          onChange={(val) => {
            setStartDate(val);
            setPage(0);
          }}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="Data fim"
          value={endDate}
          onChange={(val) => {
            setEndDate(val);
            setPage(0);
          }}
          slotProps={{ textField: { size: "small" } }}
        />
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha("#2563eb", 0.04) }}>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Ingrediente</TableCell>
              <TableCell align="right">Quantidade</TableCell>
              <TableCell align="right">Custo Unit.</TableCell>
              <TableCell align="right">Antes</TableCell>
              <TableCell align="right">Depois</TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Obs.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  Carregando...
                </TableCell>
              </TableRow>
            ) : movements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  Nenhuma movimentacao encontrada.
                </TableCell>
              </TableRow>
            ) : (
              movements.map((mov) => {
                const isPositive = Number(mov.quantity) > 0;
                return (
                  <TableRow key={mov.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(mov.created_at).format("DD/MM/YYYY HH:mm")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={TYPE_LABELS[mov.movement_type] || mov.movement_type}
                        size="small"
                        color={TYPE_COLORS[mov.movement_type] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {mov.ingredient_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.5}
                      >
                        {isPositive ? (
                          <ArrowUpIcon sx={{ fontSize: 14, color: "success.main" }} />
                        ) : (
                          <ArrowDownIcon sx={{ fontSize: 14, color: "error.main" }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={isPositive ? "success.main" : "error.main"}
                        >
                          {formatNumber(mov.quantity)} {mov.ingredient_unit}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{formatMoney(mov.unit_cost)}</TableCell>
                    <TableCell align="right">
                      {formatNumber(mov.before_quantity)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(mov.after_quantity)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                        {mov.reference || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                        {mov.supplier_name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {mov.notes || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por pagina"
        />
      </TableContainer>

      {/* Modals */}
      <StockEntryModal
        open={entryModalOpen}
        onClose={() => setEntryModalOpen(false)}
        onSuccess={handleEntrySuccess}
        showAlert={showAlert}
      />
      <StockAdjustmentModal
        open={adjustmentModalOpen}
        onClose={() => setAdjustmentModalOpen(false)}
        onSuccess={handleAdjustmentSuccess}
        showAlert={showAlert}
      />
    </Box>
  );
};

export default MovementsPage;
